import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import '../styles/BackFill.css';
import 'font-awesome/css/font-awesome.min.css';
import { MessageBar, MessageBarType } from "@fluentui/react";
import { WorkQueueList } from './WorkQueueList.tsx';
import { MetricNames, MetricCPU, MetricWaits, MetricExceptions, MetricMemory, MetricSqlCount, MetricSqlDuration, MetricULS } from '../utils/constants';
import { apiFetch } from '../lib/api-client';

export function BackFillView() {
    const [state, setState] = useState({
        selectedService: '',
        selectedProcess: '',
        selectedMetric: '',
        selectedEnv: '',
        targetDate: '',
        boxes: [],
        message: '',
        messageType: undefined,
        activeTab: 'backfill',
        services: [],
        selectAll: false,
    });

    useEffect(() => {
        window.document.title = 'PR Guardian - BackFill';
    }, []);

    const fetchServices = useCallback(async () => {
        try {
            const response = await apiFetch('/api/config');
            if (response.ok) {
                const config = await response.json();
                const servicesArray = Object.keys(config.services).map(key => ({
                    id: parseInt(key),
                    ...config.services[key],
                }));
                setState(prev => ({ ...prev, services: servicesArray }));
            } else {
                console.error('Failed to fetch config. Status:', response.status);
                setState(prev => ({ ...prev, services: [] }));
            }
        } catch (error) {
            console.error('Error fetching config:', error);
        }
    }, []);

    useEffect(() => {
        fetchServices();
    }, [fetchServices]);

    const handleStateChange = (key, value) => {
        setState(prev => ({ ...prev, [key]: value }));
    };

    const handleServiceChange = (event) => {
        const serviceId = event.target.value;
        const selectedServiceObj = state.services.find(service => service.id === parseInt(serviceId));

        const triggers = selectedServiceObj
            ? Object.keys(selectedServiceObj.triggers).map((triggerKey, index) => ({
                id: index + 1,
                label: triggerKey,
                active: false,
                tooltip: null
            }))
            : [];

        triggers.push( // Manually adding ones that are not in config.json
            {
                id: triggers.length + 1,
                label: "FeatureFlagTrigger",
                active: false,
                tooltip: "This is a stateful task.\nIf you try to add a date that is earlier than the current work item state,\nit won't be added to the Workqueue."
            },
            {
                id: triggers.length + 2,
                label: "RegressionFinderTrigger",
                active: false,
                tooltip: null
            }
        );

        if (selectedServiceObj?.ulsConfigured) {
            for (const label of ['UlsPreCommitTrigger', 'UlsPostCommitTrigger'])
                triggers.push({
                    id: triggers.length + 1,
                    label,
                    active: false,
                    tooltip: label === 'UlsPostCommitTrigger'
                        ? 'Queues all 24 UTC hours of the selected day. Creator notifications are suppressed.'
                        : 'Queries the configured multi-day precommit history in one task. Creator notifications are suppressed.',
                });
        }

        setState(prev => ({
            ...prev,
            selectedService: serviceId,
            boxes: triggers,
        }));
    };


    const handleBoxClick = (id) => {
        setState(prev => ({
            ...prev,
            boxes: prev.boxes.map(box => (box.id === id ? { ...box, active: !box.active } : box)),
        }));
    };

    const handleSelectAllChange = () => {
        setState(prev => ({
            ...prev,
            selectAll: !prev.selectAll,
            boxes: prev.boxes.map(box => ({ ...box, active: !prev.selectAll })),
        }));
    };

    const handleSubmit = async () => {
        const { selectedService, selectedProcess, selectedMetric, selectedEnv, targetDate, boxes, services } = state;
        const selectedBoxes = boxes.filter(box => box.active).map(box => box.label);

        if (!selectedService) {
            alert('Please select a service.');
            return;
        }

        const selectedServiceObj = services.find(service => service.id === parseInt(selectedService));
        if (!selectedServiceObj) {
            alert('Invalid service selected.');
            return;
        }

        const processIds = selectedProcess
            ? [selectedProcess]
            : Object.keys(selectedServiceObj.processes).map(key => selectedServiceObj.processes[key].id);

        const metricIds = selectedMetric
            ? [selectedMetric]
            : processIds.reduce((acc, processId) => {
                const process = Object.keys(selectedServiceObj.processes)
                    .map(key => selectedServiceObj.processes[key])
                    .find(p => p.id === Number(processId));

                if (process && process.metrics) {
                    Object.keys(process.metrics).forEach(metricKey => {
                        acc.push(process.metrics[metricKey].id);
                    });
                }

                return acc;
            }, []);

        const envIds = selectedEnv
            ? [selectedEnv]
            : Object.keys(selectedServiceObj.environments).map(key => selectedServiceObj.environments[key].id);

        let queuedRequests = 0;
        for (const processId of processIds) {
            for (const metricId of new Set(metricIds)) {
                for (const envId of envIds) {
                    const process = Object.keys(selectedServiceObj.processes)
                        .map(key => selectedServiceObj.processes[key]).find(value => value.id === Number(processId));
                    const validUls = selectedServiceObj.ulsConfigured && Number(metricId) === MetricULS
                        && Object.values(process?.metrics ?? {}).some(metric => metric['id'] === MetricULS)
                        && ((Number(processId) === 0 && process?.role === 'USR')
                            || (Number(processId) === 1 && process?.role === 'BOT'));
                    const tasks = selectedBoxes.filter(task =>
                        task === 'UlsPreCommitTrigger' || task === 'UlsPostCommitTrigger' ? validUls : Number(metricId) !== MetricULS);
                    if (!tasks.length) continue;
                    const navState = {
                        serviceId: selectedService,
                        processId,
                        metricId,
                        envId,
                        targetDate,
                        listoftasks: tasks.join(','),
                    };

                    const response = await queueBackfill(navState);
                    queuedRequests++;
                    if (response.status === 200) {
                        handleStateChange('message', response.data.message);
                        handleStateChange('messageType', MessageBarType.success);
                    } else {
                        handleStateChange('message', response.message || 'Failed to insert backfill workitems to WorkQueue.');
                        handleStateChange('messageType', MessageBarType.error);
                    }
                }
            }
        }
        if (!queuedRequests) {
            handleStateChange('message', 'No eligible tasks match the selected process and metric.');
            handleStateChange('messageType', MessageBarType.error);
        }
    };

    return (
        <div className="BackFill-ListView">
            {state.message && (
                <MessageBar messageBarType={state.messageType} onDismiss={() => handleStateChange('message', '')}>
                    {state.message}
                </MessageBar>
            )}
            <div className="BackFill-containerStyles">
                <div className="BackFill-tab-container">
                    <button
                        className={`BackFill-tab ${state.activeTab === 'backfill' ? 'active' : ''}`}
                        onClick={() => handleStateChange('activeTab', 'backfill')}
                    >
                        Ad hoc Backfill
                    </button>
                    <button
                        className={`BackFill-tab ${state.activeTab === 'workqueue' ? 'active' : ''}`}
                        onClick={() => handleStateChange('activeTab', 'workqueue')}
                    >
                        View WorkQueue
                    </button>
                </div>
                <div className="BackFill-content">
                    {state.activeTab === 'backfill' ? (
                        <>
                            <TopNavControls
                                selectedService={state.selectedService}
                                onServiceChange={handleServiceChange}
                                selectedProcess={state.selectedProcess}
                                onProcessChange={e => handleStateChange('selectedProcess', e.target.value)}
                                selectedEnv={state.selectedEnv}
                                setSelectedEnv={value => handleStateChange('selectedEnv', value)}
                                selectedMetric={state.selectedMetric}
                                setSelectedMetric={value => handleStateChange('selectedMetric', value)}
                                targetDate={state.targetDate}
                                setTargetDate={value => handleStateChange('targetDate', value)}
                                services={state.services}
                            />
                            <label className="select-all-label">
                                <input
                                    type="checkbox"
                                    className="select-all-checkbox"
                                    checked={state.selectAll}
                                    onChange={handleSelectAllChange}
                                />
                                Select All
                            </label>
                            <div className="warning-message">
                                <small>
                                    <i>
                                        If you don't select a process, metric, or environment, backfill will run for all available options.
                                    </i>
                                </small>
                            </div>
                            <div className="BackFill-selection-container">
                                {state.boxes.map(box => (
                                    <div
                                        key={box.id}
                                        className={`BackFill-box-item ${box.active ? 'active' : ''}`}
                                        onClick={() => handleBoxClick(box.id)}
                                        {...(box.tooltip ? { title: box.tooltip } : {})}
                                    >
                                        {box.label}
                                    </div>
                                ))}
                            </div>

                            <div className="BackFill-submit-button-container">
                                <button onClick={handleSubmit} className="BackFill-submit-button">
                                    Queue
                                </button>
                            </div>
                        </>
                    ) : (
                        <WorkQueueList />
                    )}
                </div>
            </div>
        </div>
    );
}

async function queueBackfill(navState) {
    const apiUrl = '/api/backfill';
    try {
        const response = await apiFetch(apiUrl, {
            method: 'POST',
            body: JSON.stringify(navState),
        });

        if (response.ok) {
            const data = await response.json();
            return { data, status: response.status };
        } else {
            const errorMessage = await response.text();
            return { data: null, status: response.status, message: errorMessage };
        }
    } catch (error) {
        return { data: null, status: 500, message: 'Network error' };
    }
}


function TopNavControls({
    selectedService,
    onServiceChange,
    selectedProcess,
    onProcessChange,
    selectedEnv,
    setSelectedEnv,
    selectedMetric,
    setSelectedMetric,
    targetDate,
    setTargetDate,
    services
}) {
    const selectedServiceObj = services.find(service => service.id === parseInt(selectedService));

    return (
        <div className="BackFill-topnav-container">
            <div className="BackFill-filter-container">
                <div className="BackFill-filter-item">
                    <select className="BackFill-filter-dropdown" value={selectedService} onChange={onServiceChange}>
                        <option value="" disabled hidden>
                            Select a service
                        </option>
                        {services.map(service => (
                            <option key={service.id} value={service.id}>
                                {service.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="BackFill-filter-item">
                    <select className="BackFill-filter-dropdown" value={selectedProcess} onChange={onProcessChange}>
                        <option value="" disabled hidden>
                            Select a process
                        </option>
                        {selectedServiceObj?.processes &&
                            Object.keys(selectedServiceObj.processes).map(key => {
                                const process = selectedServiceObj.processes[key];
                                return (
                                    <option key={process.id} value={process.id}>
                                        {process.friendlyName}
                                    </option>
                                );
                            })}
                    </select>
                </div>
                <div className="BackFill-filter-item">
                    <select className="BackFill-filter-dropdown" value={selectedEnv} onChange={e => setSelectedEnv(e.target.value)}>
                        <option value="" disabled hidden>
                            Select an environment
                        </option>
                        {selectedServiceObj?.environments &&
                            Object.keys(selectedServiceObj.environments).map(key => {
                                const env = selectedServiceObj.environments[key];
                                return (
                                    <option key={env.id} value={env.id}>
                                        {env.name}
                                    </option>
                                );
                            })}
                    </select>
                </div>
                <div className="BackFill-filter-item">
                    <select className="BackFill-filter-dropdown" value={selectedMetric} onChange={e => setSelectedMetric(e.target.value)}>
                        <option value="" disabled hidden>
                            Select a metric
                        </option>
                        <option value={MetricCPU}>{MetricNames[MetricCPU]}</option>
                        <option value={MetricWaits}>{MetricNames[MetricWaits]}</option>
                        <option value={MetricExceptions}>{MetricNames[MetricExceptions]}</option>
                        <option value={MetricMemory}>{MetricNames[MetricMemory]}</option>
                        <option value={MetricSqlCount}>{MetricNames[MetricSqlCount]}</option>
                        <option value={MetricSqlDuration}>{MetricNames[MetricSqlDuration]}</option>
                        {selectedServiceObj?.ulsConfigured && <option value={MetricULS}>{MetricNames[MetricULS]}</option>}
                    </select>
                </div>
                <div className="BackFill-filter-item">
                    <div className="BackFill-search-set">
                        <label className="BackFill-date-filter-dropdown">Target Date</label>
                        <input
                            className="BackFill-date-search-input"
                            type="date"
                            onChange={e => setTargetDate(e.target.value)}
                            value={targetDate}
                            max={new Date().toISOString().split('T')[0]}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}